import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import ProductMatchingPage from './pages/ProductMatchingPage';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';

// Navigation component with logout functionality
const Navigation = () => {
  const { currentUser, logout } = useAuth();
  const [isLandscapeLocked, setIsLandscapeLocked] = useState(false);
  const [isApiSupported, setIsApiSupported] = useState(false);
  
  // Check if the Screen Orientation API is supported
  useEffect(() => {
    const checkApiSupport = () => {
      try {
        // Full API support check
        if (
          window.screen && 
          window.screen.orientation && 
          typeof window.screen.orientation.lock === 'function' &&
          typeof window.screen.orientation.unlock === 'function'
        ) {
          setIsApiSupported(true);
        } else {
          setIsApiSupported(false);
        }
      } catch (error) {
        console.error('Error checking Screen Orientation API:', error);
        setIsApiSupported(false);
      }
    };
    
    checkApiSupport();
  }, []);
  
  if (!currentUser) return null;
  
  // Function to toggle landscape orientation lock
  const toggleOrientationLock = () => {
    if (!isApiSupported) {
      // Show alert if API is not supported
      alert('Screen Orientation API is not supported on this device. Please rotate your device manually.');
      return;
    }
    
    try {
      if (isLandscapeLocked) {
        // Unlock orientation
        window.screen.orientation.unlock();
        console.log('Orientation unlocked');
        setIsLandscapeLocked(false);
      } else {
        // Lock to landscape mode
        window.screen.orientation.lock('landscape')
          .then(() => {
            console.log('Orientation locked to landscape');
            setIsLandscapeLocked(true);
          })
          .catch(error => {
            console.error('Error locking orientation:', error);
            alert('Unable to lock orientation: ' + error.message);
          });
      }
    } catch (error) {
      console.error('Error accessing screen orientation:', error);
      alert('Error accessing screen orientation: ' + error.message);
    }
  };
  
  return (
    <div className="navigation">
      <div className="user-info">
        <span>Logged in as: {currentUser.name}</span>
      </div>
      <div className="nav-buttons">
        <button 
          className={`orientation-button ${isLandscapeLocked ? 'locked' : 'unlocked'} ${!isApiSupported ? 'disabled' : ''}`} 
          onClick={toggleOrientationLock}
          title={isApiSupported ? 
            (isLandscapeLocked ? 'Unlock screen rotation' : 'Lock screen to landscape mode') : 
            'Screen Orientation API not supported on this device'
          }
        >
          <i className="landscape-icon"></i>
          {isLandscapeLocked ? 'Unlock Rotation' : 'Lock Landscape'}
        </button>
        <button className="logout-button" onClick={logout}>
          Logout
        </button>
      </div>
    </div>
  );
};

function AppContent() {
  const { login } = useAuth();
  
  return (
    <div className="App">
      <Navigation />
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={login} />} />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <ProductMatchingPage />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
